import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Send } from 'lucide-react';
import { AppContext } from '../../../context/AppContext';
import {
  postMultipartWithAuthCallWithErrorResponse,
  simpleGetCallWithErrorResponse,
} from '../../../api/ApiServices';
import { Button, Card, Input, Select, Textarea } from '../../../components/ui';
import { ToastMsg } from '../../TostMsg';
import styles from './PushCampaigns.module.scss';

const ROLE = 'CRM Admin';
const PLATFORM = 'Push';

const TITLE_MIN = 3;
const TITLE_MAX = 100;
const MESSAGE_MIN = 5;
const MESSAGE_MAX = 500;

const initialForm = {
  messageTitle: '',
  message: '',
  messageGroupId: '',
  scheduledDate: '',
  scheduledTime: '',
  isTestMessage: false,
  attachmentUrl: '',
  deepLink: '',
  isSilent: false,
};

const isHttpUrl = (val) => {
  try {
    const u = new URL(val);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const isDeepLink = (val) => /^[a-z][a-z0-9+\-.]*:\/\/.+/i.test(val);

const validateField = (name, value, form) => {
  switch (name) {
    case 'messageTitle': {
      const v = (value ?? '').trim();
      if (!v) return 'Message title is required.';
      if (v.length < TITLE_MIN) return `Title must be at least ${TITLE_MIN} characters.`;
      if (v.length > TITLE_MAX) return `Title cannot exceed ${TITLE_MAX} characters.`;
      return '';
    }
    case 'message': {
      const v = (value ?? '').trim();
      if (!v) return 'Message body is required.';
      if (v.length < MESSAGE_MIN) return `Message must be at least ${MESSAGE_MIN} characters.`;
      if (v.length > MESSAGE_MAX) return `Message cannot exceed ${MESSAGE_MAX} characters.`;
      return '';
    }
    case 'messageGroupId':
      if (!value) return 'Please select a target group.';
      return '';
    case 'attachmentUrl': {
      const v = (value ?? '').trim();
      if (!v) return '';
      if (!isHttpUrl(v)) return 'Enter a valid http(s) URL.';
      return '';
    }
    case 'deepLink': {
      const v = (value ?? '').trim();
      if (!v) return '';
      if (!isDeepLink(v)) return 'Use the format scheme://path (e.g. zamzam://offers/diwali).';
      return '';
    }
    case 'scheduledDate':
    case 'scheduledTime': {
      const date = name === 'scheduledDate' ? value : form.scheduledDate;
      const time = name === 'scheduledTime' ? value : form.scheduledTime;
      if (!date && !time) return '';
      if (date && !time) return 'Pick a time to go with the date.';
      if (time && !date) return 'Pick a date to go with the time.';
      const dt = new Date(`${date}T${time}`);
      if (isNaN(dt.getTime())) return 'Enter a valid date and time.';
      if (dt.getTime() < Date.now() - 60 * 1000) return 'Scheduled time cannot be in the past.';
      return '';
    }
    default:
      return '';
  }
};

export default function PushCampaignCreate() {
  const { sidebar, ApiConfigg } = useContext(AppContext);
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const url = ApiConfigg?.GET_PROMOTION_GROUPS;
    if (!url) return;
    const qs = [
      `platform=${encodeURIComponent(PLATFORM)}`,
      `page=1`,
      `page_size=100`,
      `role=${encodeURIComponent(ROLE)}`,
    ].join('&');
    setGroupsLoading(true);
    simpleGetCallWithErrorResponse(`${url}?${qs}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.groups)
          ? json.data.groups
          : Array.isArray(json) ? json : [];
        setGroups(list);
      })
      .catch((err) => console.error('Push groups fetch error:', err))
      .finally(() => setGroupsLoading(false));
  }, [ApiConfigg?.GET_PROMOTION_GROUPS]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: next }));
    setErrors((prev) => {
      if (!prev[name] && !(name === 'scheduledDate' || name === 'scheduledTime')) return prev;
      const updated = { ...prev };
      delete updated[name];
      if (name === 'scheduledDate') delete updated.scheduledTime;
      if (name === 'scheduledTime') delete updated.scheduledDate;
      return updated;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value, form);
    setErrors((prev) => {
      const updated = { ...prev };
      if (err) updated[name] = err;
      else delete updated[name];
      return updated;
    });
  };

  const validateAll = () => {
    const fields = [
      'messageTitle',
      'message',
      'messageGroupId',
      'attachmentUrl',
      'deepLink',
      'scheduledDate',
      'scheduledTime',
    ];
    const next = {};
    fields.forEach((name) => {
      const err = validateField(name, form[name], form);
      if (err) next[name] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildScheduledParts = () => {
    const pad = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const fmtTime = (d) =>
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    if (form.scheduledDate && form.scheduledTime) {
      const dt = new Date(`${form.scheduledDate}T${form.scheduledTime}`);
      if (!isNaN(dt.getTime())) {
        return { date: fmtDate(dt), time: fmtTime(dt) };
      }
    }
    const now = new Date();
    return { date: fmtDate(now), time: fmtTime(now) };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = ApiConfigg?.POST_PROMOTIONAL_MESSAGE;
    if (!url) {
      ToastMsg('error', 'Push messages endpoint is not configured.');
      return;
    }
    if (!validateAll()) {
      ToastMsg('error', 'Please fix the highlighted fields.');
      return;
    }

    const { date: scheduledDate, time: scheduledTime } = buildScheduledParts();
    const fd = new FormData();
    fd.append('messageTitle', form.messageTitle.trim());
    fd.append('message', form.message.trim());
    fd.append('platform', PLATFORM);
    fd.append('messageGroupId', form.messageGroupId);
    fd.append('scheduledDate', scheduledDate);
    fd.append('scheduledTime', scheduledTime);
    fd.append('isTestMessage', form.isTestMessage ? 'true' : 'false');
    if (form.attachmentUrl.trim()) fd.append('attachmentUrl', form.attachmentUrl.trim());
    if (form.deepLink.trim()) fd.append('deepLink', form.deepLink.trim());
    fd.append('isSilent', form.isSilent ? 'true' : 'false');
    fd.append('role', ROLE);

    setSubmitting(true);
    postMultipartWithAuthCallWithErrorResponse(url, fd)
      .then((res) => {
        if (!res?.error) {
          ToastMsg('success', res?.json?.message || 'Push campaign created successfully.');
          navigate('/PushCampaigns');
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to create push campaign.');
        }
      })
      .catch((err) => {
        console.error('Create push campaign error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const titleCount = form.messageTitle.length;
  const messageCount = form.message.length;

  return (
    <div className={sidebar ? 'taskMain' : 'cx-active taskMain'} id="cx-main">
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/PushCampaigns')}
            >
              Back
            </Button>
            <span className={styles.iconBadge}>
              <Bell size={20} />
            </span>
            <div className={styles.titleBlock}>
              <h1>Create Push Campaign</h1>
              <p>Compose a push notification and schedule it for a target group.</p>
            </div>
          </div>
        </div>

        <Card padding="lg" elevation="sm">
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Message details</div>
              <div className={styles.formGrid}>
                <div className="full">
                  <Input
                    id="messageTitle"
                    name="messageTitle"
                    label="Message title"
                    placeholder="e.g. Diwali Offer 50% Off"
                    value={form.messageTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={TITLE_MAX}
                    error={errors.messageTitle || ''}
                    helperText={!errors.messageTitle ? `${titleCount}/${TITLE_MAX}` : ''}
                  />
                </div>
                <div className="full">
                  <Textarea
                    id="message"
                    name="message"
                    label="Message body"
                    placeholder="Tap to grab the deal before it expires"
                    value={form.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={3}
                    maxLength={MESSAGE_MAX}
                    error={errors.message || ''}
                    helperText={!errors.message ? `${messageCount}/${MESSAGE_MAX}` : ''}
                  />
                </div>
                <div>
                  <Select
                    id="messageGroupId"
                    name="messageGroupId"
                    label="Target group"
                    value={form.messageGroupId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.messageGroupId || ''}
                    helperText={!errors.messageGroupId ? 'Loaded from active push groups.' : ''}
                  >
                    <option value="">
                      {groupsLoading ? 'Loading groups…' : 'Select a group'}
                    </option>
                    {groups.map((g, idx) => {
                      const id = g.groupId ?? g.group_id ?? g.id ?? g._id ?? idx;
                      const name = g.groupName ?? g.group_name ?? g.name ?? `Group ${id}`;
                      return (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </Select>
                </div>
                <div>
                  <Input
                    id="attachmentUrl"
                    name="attachmentUrl"
                    label="Attachment URL"
                    placeholder="https://..."
                    value={form.attachmentUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.attachmentUrl || ''}
                    helperText={
                      !errors.attachmentUrl
                        ? 'Optional. Absolute URL — used as the rich-push image.'
                        : ''
                    }
                  />
                </div>
                <div>
                  <Input
                    id="deepLink"
                    name="deepLink"
                    label="Deep link"
                    placeholder="zamzam://offers/diwali"
                    value={form.deepLink}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.deepLink || ''}
                    helperText={!errors.deepLink ? 'Optional. Format: scheme://path' : ''}
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Schedule</div>
              <div className={styles.formGrid}>
                <div>
                  <Input
                    id="scheduledDate"
                    name="scheduledDate"
                    type="date"
                    label="Scheduled date"
                    value={form.scheduledDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min={new Date().toISOString().slice(0, 10)}
                    error={errors.scheduledDate || ''}
                    helperText={!errors.scheduledDate ? 'Leave blank to send now.' : ''}
                  />
                </div>
                <div>
                  <Input
                    id="scheduledTime"
                    name="scheduledTime"
                    type="time"
                    label="Scheduled time"
                    value={form.scheduledTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.scheduledTime || ''}
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Delivery options</div>
              <div className={styles.formGrid}>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    name="isTestMessage"
                    checked={form.isTestMessage}
                    onChange={handleChange}
                  />
                  <span>Send as test message</span>
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    name="isSilent"
                    checked={form.isSilent}
                    onChange={handleChange}
                  />
                  <span>Silent push (no sound / banner)</span>
                </label>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/PushCampaigns')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                rightIcon={<Send size={16} />}
              >
                Create Campaign
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
